import axiosNest from '../../api/axiosNest';
import { dateTimeNoSeconds } from '../../shared/dateTime';

/**
 * Transporeon — wyłącznie pobieranie (04.11.2026, API `transporeon/`).
 * Front tylko czyta naszą bazę: raport pobrań, transporty, sekcja na karcie
 * zlecenia; import kopii komunikatów (staging) zapisuje do naszej bazy.
 * Do Transporeonu nic stąd nie idzie.
 */

/** Przełącznik funkcji — klucz z rejestru API (dane, nie przemianowujemy). */
export const FEATURE_TRANSPOREON = 'transporeonImport';

/** Klucze w magazynie poświadczeń — per spółka (dane, nie tłumaczymy). */
export const TRANSPOREON_CREDENTIAL = {
  SYSTEM: 'transporeon',
  USERNAME: 'username',
  COMPANY_ID: 'company-id',
  PASSWORD: 'password',
  CLIENT_ID: 'client-id',
} as const;

export interface TransporeonPull {
  id: number;
  companyId: number;
  origin: 'pull' | 'import' | string;
  triggeredBy: string | null;
  startedAt: string;
  finishedAt: string | null;
  messageCount: number;
  newMessages: number;
  duplicateMessages: number;
  alertsCreated: number;
  ackStatus: string | null;
  ackNote: string | null;
  acknowledgedAt: string | null;
  httpStatus: number | null;
  error: string | null;
  notes: string[];
}

export interface TransporeonState {
  production: boolean;
  acknowledge: boolean;
  everySeconds: number | null;
  /** Wyłącznik modułu `TRANSPOREON_POBIERANIE` (04.11.2026) — bez niego żadnego pobrania. */
  pullEnabled?: boolean;
  configErrors: string[];
  importAllowed: boolean;
  pulls: TransporeonPull[];
  transports: Record<string, number>;
  missingInTms: number;
  messages: number;
  pendingMessages: number;
}

export interface TransporeonTransportItem {
  id: number;
  companyId: number;
  shipperName: string | null;
  number: string;
  lastQualifier: string;
  lastStatus: string | null;
  lastMessageAt: string;
  firstSeenAt: string;
  cancelledAt: string | null;
  reissuedAt: string | null;
  transportIds: string[];
  loadingFrom: string | null;
  unloadingTo: string | null;
  route: string | null;
  matchStatus: string;
  matchNote: string | null;
  matchedUserOfferIds: number[];
  missingAlertAt: string | null;
  missingInTms: boolean;
  preOrderId: number | null;
  tpwLink: string | null;
}

export interface TransporeonPoint {
  type: string;
  country: string | null;
  zip: string | null;
  city: string | null;
  address: string | null;
  name: string | null;
  from: string | null;
  until: string | null;
}

export interface TransporeonOrderItem extends TransporeonTransportItem {
  weightKg: number | null;
  vehicle: string | null;
  points: TransporeonPoint[];
  history: {
    id: number;
    qualifier: string;
    status: string | null;
    messageAt: string | null;
    outcome: string | null;
    alertsCreated: number;
  }[];
}

export interface TransporeonRawMessage {
  id: number;
  documentReference: string;
  transportId: string;
  qualifier: string;
  status: string | null;
  messageAt: string | null;
  messageAtText: string | null;
  receivedAt: string;
  outcome: string | null;
  alertsCreated: number;
  payload: unknown;
}

export const TRANSPORT_FILTERS = [
  { key: 'all', label: 'Wszystkie' },
  { key: 'missing', label: 'Brak w TMS' },
  { key: 'multiple', label: 'Kilka zleceń w TMS' },
  { key: 'mismatch', label: 'Numer jest, dane się nie zgadzają' },
  { key: 'cancelled', label: 'Anulowane' },
] as const;

/** Rodzaj komunikatu po polsku — kwalifikator zostaje obok dosłownie. */
export function qualifierLabel(q: string): string {
  switch (q) {
    case 'tour.assign':
      return 'przydzielony';
    case 'tour.accepted':
      return 'przyjęty';
    case 'tour.assigned.change':
      return 'zmiana';
    case 'tour.assigned.cancelled':
      return 'anulowany';
    default:
      return q.startsWith('tour.') ? 'przetarg / oferta (tylko zapis)' : q || '—';
  }
}

export function matchLabel(status: string): string {
  switch (status) {
    case 'matched':
      return 'jest w TMS';
    case 'multiple':
      return 'kilka zleceń w TMS';
    case 'mismatch':
      return 'numer jest, dane się nie zgadzają';
    case 'none':
      return 'brak w TMS';
    default:
      return 'czeka na dopasowanie';
  }
}

export function ackLabel(p: Pick<TransporeonPull, 'ackStatus' | 'origin'>): string {
  if (p.origin === 'import') return 'kopia — bez kolejki';
  switch (p.ackStatus) {
    case 'acknowledged':
      return 'potwierdzone';
    case 'skipped':
      return 'bez potwierdzenia';
    case 'failed':
      return 'potwierdzenie nie wyszło';
    default:
      return '—';
  }
}

export async function fetchTransporeonState(): Promise<TransporeonState> {
  return (await axiosNest.get('/transporeon/state')).data as TransporeonState;
}

export async function fetchTransporeonTransports(
  filter: string,
  page = 0,
): Promise<{ total: number; items: TransporeonTransportItem[] }> {
  return (await axiosNest.get('/transporeon/transports', { params: { filter, page, perPage: 50 } })).data;
}

export async function fetchTransporeonMessages(id: number): Promise<TransporeonRawMessage[]> {
  return (await axiosNest.get(`/transporeon/transports/${id}/messages`)).data as TransporeonRawMessage[];
}

export async function fetchTransporeonForOrder(
  userOfferId: number | string,
): Promise<{ source: 'transporeon'; items: TransporeonOrderItem[] }> {
  return (await axiosNest.get(`/transporeon/orders/${userOfferId}`)).data;
}

export async function importTransporeonCopy(file: File, companyId?: number): Promise<unknown> {
  const form = new FormData();
  form.append('file', file);
  return (
    await axiosNest.post('/transporeon/import', form, {
      params: companyId ? { companyId } : {},
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  ).data;
}

export async function downloadTransporeonExport(since: string): Promise<void> {
  const res = await axiosNest.get('/transporeon/export', { params: { since } });
  const blob = new Blob([JSON.stringify(res.data)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `transporeon-komunikaty-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── „Transporeon — brak w TMS” dla FTL (decyzja Bartka 04.11.2026) ──

/** Zasób (ścieżka) listy dla FTL w `<Admin>` — kafelek pulpitów FTL tu prowadzi. */
export const MISSING_IN_TMS_RESOURCE = 'transporeon-missing';

/** Świeżość danych — pobieranie wstrzymane, liczba to stan z bazy. */
export interface TransporeonFreshness {
  /** Chwila najnowszych danych (UTC); `null` — nic jeszcze nie przyszło. */
  dataAsOf: string | null;
  /** Dane starsze niż godzina albo brak danych. */
  stale: boolean;
  /** `false` — pobieranie z Transporeonu wstrzymane. */
  pullEnabled: boolean;
}

/** Wiersz listy dla FTL — wyłącznie te pola (bez dyspozytorów i surowej treści). */
export interface MissingInTmsItem {
  id: number;
  shipperName: string | null;
  number: string;
  route: string | null;
  loadingFrom: string | null;
  loadingUntil: string | null;
  missingSince: string;
  /** Spółka transportu (05.11.2026). */
  companyId: number;
  companyName: string | null;
}

export interface MissingInTmsList {
  total: number;
  items: MissingInTmsItem[];
  freshness: TransporeonFreshness;
  /** Zastosowany filtr spółki; `null` — wszystkie spółki. */
  companyId: number | null;
  /** Spółki do wyboru — nazwy z kartoteki spółek. */
  companies: { id: number; name: string }[];
}

/** Liczba jednej spółki przy kafelku konta bez spółki (05.11.2026). */
export interface MissingInTmsCompanyCount {
  companyId: number;
  companyName: string;
  count: number;
  freshness: TransporeonFreshness;
}

/** Wartość filtra spółki „wszystkie spółki” — jak w API (klucz `companyId`). */
export const ALL_COMPANIES = 'all';

/** Adres listy z filtrem spółki — liczba spółki albo `all`; bez — domyślny (spółka konta). */
export function missingInTmsUrl(companyId?: number | string | null): string {
  return companyId === undefined || companyId === null || companyId === ''
    ? `/${MISSING_IN_TMS_RESOURCE}`
    : `/${MISSING_IN_TMS_RESOURCE}?companyId=${encodeURIComponent(String(companyId))}`;
}

/** „Spółka A: 3 · Spółka B: 0” — rozbicie przy kafelku konta bez spółki. */
export function companyBreakdownText(rows: readonly MissingInTmsCompanyCount[] | null | undefined): string | null {
  if (!rows || rows.length === 0) return null;
  return rows.map((r) => `${r.companyName}: ${r.count}`).join(' · ');
}

/**
 * Dopisek przy liczbie: „Dane z Transporeonu z dnia 03.11.2026, 09:00”, gdy
 * dane są starsze niż godzina; bez danych — że nic jeszcze nie przyszło.
 * Aktualne dane — bez dopisku. Czas polski.
 */
export function freshnessNote(f: TransporeonFreshness | null | undefined): string | null {
  if (!f || !f.stale) return null;
  const hold = f.pullEnabled ? '' : ' (pobieranie wstrzymane)';
  return f.dataAsOf
    ? `Dane z Transporeonu z dnia ${dateTimeNoSeconds(f.dataAsOf)}${hold}`
    : `Brak danych z Transporeonu${hold}`;
}

export async function fetchMissingInTms(
  page = 0,
  perPage = 50,
  companyId?: string | null,
): Promise<MissingInTmsList> {
  const { data } = await axiosNest.get<MissingInTmsList>('/transporeon/missing-in-tms', {
    params: companyId ? { page, perPage, companyId } : { page, perPage },
  });
  return {
    total: data?.total ?? 0,
    items: Array.isArray(data?.items) ? data.items : [],
    freshness: data?.freshness ?? { dataAsOf: null, stale: true, pullEnabled: false },
    companyId: typeof data?.companyId === 'number' ? data.companyId : null,
    companies: Array.isArray(data?.companies) ? data.companies : [],
  };
}
